import { Spinner } from '#lib/components/loading.jsx';
import { cn, mergeClasses } from '#lib/helpers/class.js';
import React, { Fragment, useEffect } from 'react';
import FloatingTooltip from '#components/tooltip.jsx';
import { Icon } from '@iconify-icon/react';
import validateFile from '#lib/helpers/file.js';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export function DocumentList(props) {
    const { children } = props;
    return <ul className="max-h-[600px] overflow-auto">{children}</ul>;
}

function _Item(props) {
    const { onClick, className, children } = props;

    return (
        <li
            className={mergeClasses('relative text-neutral-800 flex items-center pl-2 pr-1 py-1 mb-3', className)}
            onClick={onClick}
        >
            {children}
        </li>
    );
}

function PreviewFrame(props) {
    const { children, className } = props;
    return (
        <div
            className={mergeClasses(
                'w-20 h-16 rounded overflow-hidden bg-neutral-50 border border-neutral-200 block flex justify-center items-center',
                className
            )}
        >
            {children}
        </div>
    );
}

export function DocumentLoadingPreview() {
    return (
        <PreviewFrame>
            <Spinner />
        </PreviewFrame>
    );
}

export function DocumentMiniPreview(props) {
    const { onClick, onEdit, onDelete, name, visible, active, isLoading } = props;

    const [isEditing, setIsEditing] = React.useState(false);

    const ref = React.useRef();
    useEffect(() => {
        if (isEditing) {
            ref.current.focus();
            ref.current.style.padding = '0 2px';
        }
    }, [isEditing]);

    const handleDelete = () => {
        window.confirm(_t.global.confirmDelete()) && onDelete();
    };

    return (
        <Fragment>
            <div
                className={cn(
                    'w-full h-full flex gap-x-3 cursor-pointer',
                    !active && 'hover:text-primary-600 active:text-primary-700',
                    active && 'text-primary-600'
                )}
                onClick={onClick}
            >
                <Icon icon="mdi:file-document-outline" width="1.2rem" height="1.2rem" />

                <div className="w-4/5 h-full items-center">
                    <span
                        ref={ref}
                        contentEditable={isEditing}
                        onBlur={() => {
                            setIsEditing(false);
                            onEdit('name', ref.current.textContent);
                        }}
                        className={cn(isEditing && 'cursor-text')}
                    >
                        {name}
                    </span>
                </div>
            </div>
            <div className="absolute flex gap-x-[6px] right-1">
                {isLoading && <Spinner className="absolute h-5 w-5 right-[25px] block" />}

                <>
                    {visible ? (
                        <FloatingTooltip content={_t.global.hide()} placement="top">
                            <EyeIcon
                                onClick={() => {
                                    onEdit('visible', false);
                                }}
                                className="cursor-pointer icon-sm"
                            />
                        </FloatingTooltip>
                    ) : (
                        <FloatingTooltip content={_t.global.show()} placement="top">
                            <EyeSlashIcon
                                onClick={() => {
                                    onEdit('visible', true);
                                }}
                                className="cursor-pointer icon-sm"
                            />
                        </FloatingTooltip>
                    )}
                </>
                <FloatingTooltip content={_t.global.delete()} placement="top">
                    <Icon
                        className="cursor-pointer"
                        icon="ph:trash"
                        width="1.3rem"
                        height="1.3rem"
                        onClick={handleDelete}
                    />
                </FloatingTooltip>
                <FloatingTooltip content={_t.global.edit()} placement="top">
                    <Icon
                        icon="material-symbols:edit-square-outline-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        className="cursor-pointer pt-[1px] text-neutral-800"
                        onClick={() => setIsEditing(true)}
                    />
                </FloatingTooltip>
            </div>
        </Fragment>
    );
}

function AddItem(props) {
    const { onAdd, children, className, accept, ...rest } = props;

    const onChange = (e) => {
        const { files } = e.target;
        const filesArray = Array.from(files);
        const filteredArray = filesArray.filter((file) => validateFile(file.type, accept));
        if (filteredArray.length < filesArray.length) toast.error(_t.global.invalidFileType());
        if (filteredArray.length) onAdd(e, filteredArray);
    };

    return (
        <_Item
            className={mergeClasses(
                'relative w-full flex justify-center items-center gap-x-2 px-4 py-1 mb-0 text-neutral-600 rounded border-2 border-dashed border-neutral-300 hover:bg-white hover:border-solid cursor-pointer',
                className
            )}
        >
            <input
                className="absolute inset-0 opacity-0 cursor-pointer z-10"
                type="file"
                value={''}
                onChange={onChange}
                accept={accept}
                {...rest}
            />
            <div className="h-full flex items-center">{children || _t.edit.planner.new()}</div>
            <div className="h-full flex items-center">
                <Icon
                    icon="material-symbols:add"
                    width="1.2rem"
                    height="1.2rem"
                    className="-translate-x-2 translate-y-0.5"
                />
            </div>
        </_Item>
    );
}

DocumentList.Item = _Item;
DocumentList.AddItem = AddItem;
